import React from "react";
import { Monitor, Palette, Check, Globe, Bell, Save } from "lucide-react";
import ZonasHorarias from "../components/ZonasHorarias";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebaseConfig";
import { doc, onSnapshot, setDoc, Timestamp } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import NotificationsPortal, { notify } from "../components/NotificationsPortal";

const temas = [
  { id: "light", labelKey: "personalization.theme.options.light" },
  { id: "dark", labelKey: "personalization.theme.options.dark" },
  { id: "auto", labelKey: "personalization.theme.options.auto" },
];

const colores = [
  {
    id: "blue",
    labelKey: "personalization.accent.options.blue",
    color: "#2563EB",
  },
  {
    id: "indigo",
    labelKey: "personalization.accent.options.indigo",
    color: "#4F46E5",
  },
  {
    id: "green",
    labelKey: "personalization.accent.options.green",
    color: "#16A34A",
  },
  {
    id: "purple",
    labelKey: "personalization.accent.options.purple",
    color: "#9333EA",
  },
  {
    id: "pink",
    labelKey: "personalization.accent.options.pink",
    color: "#DB2777",
  },
  {
    id: "red",
    labelKey: "personalization.accent.options.red",
    color: "#DC2626",
  },
  {
    id: "orange",
    labelKey: "personalization.accent.options.orange",
    color: "#F97316",
  },
  {
    id: "yellow",
    labelKey: "personalization.accent.options.yellow",
    color: "#EAB308",
  },
  {
    id: "cyan",
    labelKey: "personalization.accent.options.cyan",
    color: "#06B6D4",
  },
  {
    id: "emerald",
    labelKey: "personalization.accent.options.emerald",
    color: "#059669",
  },
];

export default function Personalizacion() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const { theme, setTheme, setAppTimeZone, timeZone, accent, setAccent } =
    useTheme();
  const [modoCompacto, setModoCompacto] = React.useState(false);
  const [notificaciones, setNotificaciones] = React.useState(true);
  const [alertasSonido, setAlertasSonido] = React.useState(true);
  const [idioma, setIdioma] = React.useState(i18n.language || "es");
  const [formatoFecha, setFormatoFecha] = React.useState("DD/MM/YYYY");
  const [moneda, setMoneda] = React.useState("MXN");

  // ===== Cargar desde Firestore (igual que FiscalPanel) =====
  React.useEffect(() => {
    if (!user?.uid) return;

    const ref = doc(db, "users", user.uid, "prefs", "ui");
    const unsub = onSnapshot(ref, (snap) => {
      const pref = snap.exists() ? snap.data() || {} : {};

      setModoCompacto(!!pref.compactMode);
      setNotificaciones(pref.notifications ?? true);
      setAlertasSonido(pref.soundAlerts ?? true);

      const dbIdioma = pref.language ?? "es";
      const dbFormato = pref.dateFormat ?? "DD/MM/YYYY";
      const dbMoneda = pref.currency ?? "MXN";

      setIdioma(dbIdioma);
      setFormatoFecha(dbFormato);
      setMoneda(dbMoneda);

      // aplica theme/accent/timezone desde tu ThemeContext setters
      if (pref.theme) setTheme(pref.theme);
      if (pref.accent) setAccent(pref.accent, { persist: false });
      if (pref.timeZone) setAppTimeZone(pref.timeZone, { persist: false });

      if (i18n.language !== dbIdioma) i18n.changeLanguage(dbIdioma);
    });

    return () => unsub();
  }, [user?.uid, i18n, setTheme, setAccent, setAppTimeZone]);

  // ===== Handlers =====
  const onChangeIdioma = async (e) => {
    const val = e.target.value;
    setIdioma(val);
    await i18n.changeLanguage(val);
  };

  const onChangeZona = async (e) => {
    const val = e.target.value;
    await setAppTimeZone(val, { persist: true });
    notify("success", "Zona horaria guardada.");
  };

  const save = async () => {
    try {
      if (!user?.uid) throw new Error("No hay usuario.");

      await setDoc(
        doc(db, "users", user.uid, "prefs", "ui"),
        {
          theme,
          accent,
          compactMode: modoCompacto,
          notifications: notificaciones,
          soundAlerts: alertasSonido,
          language: idioma,
          dateFormat: formatoFecha,
          currency: moneda,
          timeZone,
          updatedAt: Timestamp.now(),
          updatedBy: user.uid,
        },
        { merge: true },
      );

      notify("success", "Cambios guardados correctamente.");
    } catch (err) {
      notify("error", `Error al guardar: ${err?.message || "—"}`);
    }
  };

  return (
    <>
      <NotificationsPortal />

      <section className="personalizacion-page">
        {/* ===== TEMA ===== */}
        <div className="settings-card">
          <div className="settings-header">
            <div className="settings-icon theme">
              <Monitor size={18} color="currentColor" />
            </div>
            <div>
              <h3>{t("personalization.theme.title")}</h3>
              <p>{t("personalization.theme.desc")}</p>
            </div>
          </div>

          <div className="theme-grid">
            {temas.map((tema) => (
              <button
                key={tema.id}
                type="button"
                onClick={() => setTheme(tema.id)}
                className={`theme-card ${theme === tema.id ? "active" : ""}`}
              >
                <div className={`theme-preview ${tema.id}`} />
                <span>{t(tema.labelKey)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ===== COLOR DE ACENTO ===== */}
        <div className="settings-card">
          <div className="settings-header">
            <div className="settings-icon accent">
              <Palette size={18} color="currentColor" />
            </div>
            <div>
              <h3>{t("personalization.accent.title")}</h3>
              <p>{t("personalization.accent.desc")}</p>
            </div>
          </div>

          <div className="accent-grid">
            {colores.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setAccent(c.id, { persist: true })}
                className={`accent-card ${accent === c.id ? "active" : ""}`}
              >
                <span
                  className="accent-dot"
                  style={{ backgroundColor: c.color }}
                >
                  {accent === c.id && <Check size={14} color="#fff" />}
                </span>
                <span className="accent-label">{t(c.labelKey)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ===== CONFIGURACIÓN REGIONAL ===== */}
        <div className="settings-card">
          <div className="settings-header">
            <div className="settings-icon regional">
              <Globe size={18} color="currentColor" />
            </div>
            <div>
              <h3>{t("personalization.regional.title")}</h3>
              <p>{t("personalization.regional.desc")}</p>
            </div>
          </div>

          <div className="fiscal-grid">
            <div className="fiscal-field">
              <label className="fiscal-label">
                {t("personalization.regional.language")}
              </label>
              <select
                className="fiscal-control"
                value={idioma}
                onChange={onChangeIdioma}
              >
                <option value="es">Español</option>
                <option value="en">English</option>
              </select>
            </div>

            <div className="fiscal-field">
              <label className="fiscal-label">
                {t("personalization.regional.timezone")}
              </label>
              <ZonasHorarias value={timeZone} onChange={onChangeZona} />
            </div>

            <div className="fiscal-field">
              <label className="fiscal-label">
                {t("personalization.regional.dateFormat")}
              </label>
              <select
                className="fiscal-control"
                value={formatoFecha}
                onChange={(e) => setFormatoFecha(e.target.value)}
              >
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              </select>
            </div>

            <div className="fiscal-field">
              <label className="fiscal-label">
                {t("personalization.regional.currency")}
              </label>
              <select
                className="fiscal-control"
                value={moneda}
                onChange={(e) => setMoneda(e.target.value)}
              >
                <option value="MXN">Peso Mexicano (MXN)</option>
                <option value="USD">Dólar Estadounidense (USD)</option>
                <option value="EUR">Euro (EUR)</option>
              </select>
            </div>
          </div>
        </div>

        {/* ===== NOTIFICACIONES ===== */}
        <div className="settings-card">
          <div className="settings-header">
            <div className="settings-icon notifications">
              <Bell size={18} color="currentColor" />
            </div>
            <div>
              <h3>{t("personalization.notifications.title")}</h3>
              <p>{t("personalization.notifications.desc")}</p>
            </div>
          </div>

          <div className="switches-container">
            <div className="switch-row divider">
              <div>
                <span className="switch-label">
                  {t("personalization.notifications.show")}
                </span>
                <p className="switch-desc">
                  {t("personalization.notifications.showDesc")}
                </p>
              </div>
              <label className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={notificaciones}
                  onChange={() => setNotificaciones(!notificaciones)}
                />
              </label>
            </div>

            <div className="switch-row">
              <div>
                <span className="switch-label">
                  {t("personalization.notifications.sound")}
                </span>
                <p className="switch-desc">
                  {t("personalization.notifications.soundDesc")}
                </p>
              </div>
              <label className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={alertasSonido}
                  onChange={() => setAlertasSonido(!alertasSonido)}
                />
              </label>
            </div>
          </div>
        </div>

        {/* ===== BOTÓN GUARDAR ===== */}
        <div className="actions-footer">
          <button className="info-save" type="button" onClick={save}>
            <Save size={18} />
            {t("actions.saveChanges")}
          </button>
        </div>
      </section>
    </>
  );
}
